import { useState } from 'react';
import calendarIcon from '@ingka/ssr-icon/paths/calendar';
import Button from '@ingka/button';
import Modal, { ModalHeader, Prompt } from '@ingka/modal';
import {
  google,
  outlook,
  office365,
  office365Mobile,
  yahoo,
  ics,
  aol,
  msTeams,
} from 'calendar-link';
import { useTranslation } from 'react-i18next';
import { styled } from 'styled-components';
import colors from '../styles/colors';
import {
  AOLIcon,
  AppleIcon,
  GoogleIcon,
  MicrosoftTeamsIcon,
  Office365Icon,
  OutlookIcon,
  YahooIcon,
} from './CalendarIcons';
import SSRIcon from '@ingka/ssr-icon';

const StyledButton = styled(Button)`
  &&& {
    span.btn__inner {
      min-height: 2.5rem;
    }

    span.btn__label {
      font-size: 0.75rem;
      line-height: 1rem;
    }
  }
`;

const StyledPrompt = styled(Prompt)`
  &&& {
    .prompt__heading {
      position: absolute;
      left: -9999px;
    }
    .prompt__content {
      padding: 0 0 28px 0;
    }
  }
`;

const CalendarOptionsList = styled.ul`
  padding: 0;
  margin: 0;
  list-style-type: none;

  li {
    padding: 6px 10px;

    &:hover {
      background-color: ${colors.neutral.grey100};
    }

    a {
      display: flex;
      align-items: center;
      padding: 10px 28px;
      text-decoration: none;
      color: ${colors.text.default};
      height: 100%;
      width: 100%;

      &:hover,
      &:focus {
        background-color: ${colors.neutral.grey100};
        text-decoration: none;
      }
    }
  }
`;

const StyledSSRIcon = styled(SSRIcon)`
  &&& {
    margin-left: -4px;
    margin-right: 12px;
  }
`;

type AddToCalenderButtonProps = {
  name: string;
  description: string;
  location: string;
  start: Date;
  end: Date;
};

const AddToCalendarButton: React.FC<AddToCalenderButtonProps> = ({
  name,
  description,
  location,
  start,
  end,
}) => {
  const { t } = useTranslation();
  const [calendarMenuIsOpen, setCalendarMenuIsOpen] = useState(false);

  const event = {
    title: name,
    description,
    location,
    start,
    end,
  };

  const isMobileBrowser = (): boolean => {
    return /Mobi|Android|iPhone|iPad|iPod|Opera Mini|IEMobile|Mobile/i.test(
      navigator.userAgent,
    );
  };

  const isIOS = (): boolean => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  };

  const googleUrl = google(event);
  const outlookUrl = outlook(event);
  const office365Url = office365(event);
  const office365MobileUrl = office365Mobile(event);
  const teamsUrl = msTeams(event);
  const yahooUrl = yahoo(event);
  const icsUrl = ics(event);
  const aolUrl = aol(event);

  return (
    <>
      <StyledButton
        data-cy="add-to-calendar-button-with-timeZone"
        type="secondary"
        text={t('pages.confirm.addToCalendarButtonText')}
        ssrIcon={calendarIcon}
        onClick={() => setCalendarMenuIsOpen(!calendarMenuIsOpen)}
      />

      <Modal
        visible={calendarMenuIsOpen}
        handleCloseBtn={() => setCalendarMenuIsOpen(!calendarMenuIsOpen)}
      >
        <StyledPrompt
          title={t('pages.confirm.calendarPromptTitle')}
          titleId="calendar-prompt-id"
          header={
            <ModalHeader
              ariaCloseTxt={t('pages.confirm.calendarPromptCloseButton')}
            />
          }
          footer={null}
        >
          <CalendarOptionsList>
            <li>
              <a href={icsUrl} target="_blank" rel="noopener noreferrer">
                <AppleIcon />
                Apple
              </a>
            </li>
            <li>
              <a href={icsUrl} target="_blank" rel="noopener noreferrer">
                <StyledSSRIcon paths={calendarIcon} />
                iCal (.ics)
              </a>
            </li>
            <li>
              <a href={googleUrl} target="_blank" rel="noopener noreferrer">
                <GoogleIcon />
                Google
              </a>
            </li>
            {/* Teams, Outlook and Office365 links don't always work on mobile, especially on iOS (linking to generic pages, stripping the dates). */}
            {/* Therefore, below we will hide calendars that don't work properly. */}
            {/* Hide Office365 on iOS, show only on desktop */}
            {isIOS() ? null : (
              <li>
                <a
                  href={isMobileBrowser() ? office365MobileUrl : office365Url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Office365Icon />
                  Office365
                </a>
              </li>
            )}
            {/* Hide Outlook on mobile browsers, show only on desktop */}
            {isMobileBrowser() ? null : (
              <li>
                <a href={outlookUrl} target="_blank" rel="noopener noreferrer">
                  <OutlookIcon />
                  Outlook
                </a>
              </li>
            )}
            {/* Hide Teams on mobile browsers, show only on desktop */}
            {isMobileBrowser() ? null : (
              <li>
                <a href={teamsUrl} target="_blank" rel="noopener noreferrer">
                  <MicrosoftTeamsIcon />
                  Teams
                </a>
              </li>
            )}
            <li>
              <a href={yahooUrl} target="_blank" rel="noopener noreferrer">
                <YahooIcon />
                Yahoo
              </a>
            </li>
            <li>
              <a href={aolUrl} target="_blank" rel="noopener noreferrer">
                <AOLIcon />
                AOL
              </a>
            </li>
          </CalendarOptionsList>
        </StyledPrompt>
      </Modal>
    </>
  );
};

export default AddToCalendarButton;
